import type { z } from 'zod';
/** Prefix for all keys in localStorage and be able to identify keys from other applications. */
export const STORAGE_PREFIX = 'brightlocal_';

/** Prefix a key with the storage prefix. */
export const prefixKey = (key: string): string => `${STORAGE_PREFIX}${key}`;

export const LocalStorage = {
  get: <T>(key: string, codec: z.ZodType<T>) => localStorageGet(key, codec),
  set: <T>(key: string, value: T) => localStorageSet(key, value),
  remove: (key: string) => localStorageRemove(key),
  onCrossTabRemove: (key: string, callback: () => void) => localStorageOnRemove(key, callback)
};

/** Read and parse a value. Returns `null` when the key is missing **or** storage is inaccessible. */
const localStorageGet = <T>(key: string, codec: z.ZodType<T>): T | null => {
  try {
    const value = localStorage.getItem(prefixKey(key));

    if (!value) return null;

    return codec.parse(JSON.parse(value));
  } catch {
    return null;
  }
};

/** Write a value. Silently fails when storage is inaccessible. */
const localStorageSet = <T>(key: string, value: T): void => {
  try {
    const json = JSON.stringify(value);

    localStorage.setItem(prefixKey(key), json);
  } catch {
    // serialization failed or storage inaccessible — nothing we can do
  }
};

/** Remove a value. Silently fails when storage is inaccessible. */
const localStorageRemove = (key: string): void => {
  try {
    localStorage.removeItem(prefixKey(key));
  } catch {
    // storage disabled — nothing we can do
  }
};

/** Listen for cross-tab removal of a key. Returns an unsubscribe function. */
const localStorageOnRemove = (key: string, callback: () => void): (() => void) => {
  const prefixed = prefixKey(key);

  const handler = (event: StorageEvent) => {
    if (event.key === prefixed && event.newValue === null) {
      callback();
    }
  };

  window.addEventListener('storage', handler);

  return () => window.removeEventListener('storage', handler);
};
